import React, { useState } from 'react';
import {
  Image,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Icon from '../../components/ui/Icon';
import Skeleton, { SkeletonText } from '../../components/ui/Skeleton';
import Text from '../../components/ui/Text';
import useSubscription from '../../hooks/useSubscription';
import { toPlanViews } from '../../services/subscriptionView';
import {
  faqs,
  highlights,

  perks,

} from '../../data/subscription';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../../theme';

/** The hero artwork. A room, not a person - the same rule the tiles follow. */
const HERO_ART = require('../../../assets/uploads/banner/plus (2).png');

/** Banner aspect, from the artwork's own 1674x939. */
/**
 * Where the hero's copy starts, measured from the top of the artwork.
 *
 * The back button is a 38pt disc at `spacing.sm`, so it ends at 46pt - but
 * the button sits at the artwork's far left and the copy is inset well past
 * it, so the two never share a horizontal band and the copy is free to rise
 * above 46 without colliding with anything.
 *
 * 40 puts it in the shot's clear upper-left, high enough that the block ends
 * before the chair and the lamp begin.
 */
const HERO_COPY_TOP = 40;

const HERO_ASPECT = 1674 / 939;

/**
 * Homingo+ - the membership page.
 *
 * ---------------------------------------------------------------------------
 * ONE PLAN ON SCREEN AT A TIME
 * ---------------------------------------------------------------------------
 * The two terms are a segmented control, and only the chosen one renders its
 * card. Showing both side by side made the page ask two questions at once -
 * "do you want this?" and "monthly or yearly?" - and answered neither well:
 * two half-width cards meant two cramped price columns, and the yearly
 * saving, which is the entire argument for the annual term, was reduced to a
 * badge with no room to say what it was worth.
 *
 * With one card the price can be the biggest thing on it, the struck-through
 * comparison fits beside it, and the toggle above carries the saving as a tag
 * so the choice is still visible from the tab that is not selected.
 *
 * ---------------------------------------------------------------------------
 * NOBODY CAN ACTUALLY JOIN, AND THE PAGE NO LONGER OFFERS TO
 * ---------------------------------------------------------------------------
 * See `data/subscription.ts`: there is no recurring-payment mandate, no
 * renewal, and no way to cancel a debit that was never set up. The page used
 * to carry a "Start membership" button that routed to an unavailable-gate;
 * that button is gone, so the page now presents the plans and what they
 * include without offering a commitment it cannot honour.
 *
 * The term picker is live anyway, and deliberately so: choosing a term is how
 * somebody decides whether the thing is worth it, and disabling the choice
 * would hide the yearly saving that is the main argument for it.
 */

type Props = {
  onBack: () => void;
  /**
   * Raise the sign-in sheet.
   *
   * The plans endpoint is behind the customer guard, so there is nothing to
   * show without a session and no point retrying until there is one.
   */
  onSignIn: () => void;
};

/** `2999` reads as `2,999`. Indian grouping, same as the rest of the app. */
const rupees = (value: number) =>
  String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

function SubscriptionScreen({ onBack, onSignIn }: Props) {
  const resp = useResponsive();
  /*
   * The device's own top inset, applied by hand to the hero's floating
   * chrome.
   *
   * This page mounts in `HomeScreen`'s absolute overlay, which sits on the
   * `SafeAreaView`'s border box and so gets none of its padding - the same
   * hole `PageHeader` plugs for every other account page. This screen has no
   * `PageHeader` (the hero bleeds to the top on purpose), so the arrow and
   * the copy must carry the inset themselves or they sit under the clock.
   */
  const insets = useSafeAreaInsets();

  /*
   * Plans and membership, from the server.
   *
   * `membershipFor()` was a stub that returned `{ active: false }` - it could
   * never report a real plan. `subscription.isMember` reads
   * `GET /subscriptions/active`, which is the ONLY proof of membership: buying
   * creates a `pending_payment` row that grants nothing until the payment
   * confirmation activates it.
   *
   * `toPlanViews` maps the server's plans into the shape this card already
   * draws - see `services/subscriptionView.ts` for what is derived and what is
   * deliberately dropped rather than invented.
   */
  const subscription = useSubscription();
  const planViews = toPlanViews(subscription.plans.data ?? []);
  const livePlan = subscription.active.data;
  const membership = {
    active: subscription.isMember,
    /* `expiresAt` is when the term ENDS. The mock called it "renews on",
       which the backend does not promise - nothing here auto-renews, so the
       copy below says expires. */
    expiresOn: livePlan?.expiresAt
      ? new Date(livePlan.expiresAt).toLocaleDateString()
      : null,
    planName: livePlan?.planName ?? null,
  };

  /* Which term is selected. Null until the plans land - there is nothing to
     preselect before then, and the recommended one is only known from data. */
  const [plan, setPlan] = useState<string | null>(null);
  /** Which FAQ is open. Only one at a time, so the page cannot unroll. */
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  /*
   * `planViews` is empty for a real stretch of every open: `plans.data` is
   * `null` until the request resolves (see `useAsync`), and `toPlanViews`
   * maps that to `[]`. `chosen` used to be read unconditionally below and the
   * card dereferenced `chosen.recommended` etc. on the very first render -
   * `chosen` was `undefined` and the screen crashed the whole app before the
   * plans ever had a chance to land. The JSX below now checks `chosen` is set
   * before drawing the card, and shows a skeleton or an error in its place.
   */
  const chosen =
    planViews.find(item => item.key === plan) ??
    planViews.find(item => item.recommended) ??
    planViews[0];

  /*
   * The hero runs the full device width, so its height follows the artwork's
   * own aspect rather than a fixed number - a hard-coded height would letterbox
   * or crop differently on every phone.
   */
  const heroWidth = resp.width;
  /* Taller by the inset the copy just moved down, so the block still ends
     before the chair and the lamp - the artwork covers the stretch. */
  const heroHeight = Math.round(heroWidth / HERO_ASPECT) + insets.top;

  return (
    <View style={styles.root}>
      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.column]}
        showsVerticalScrollIndicator={false}
      >
        {/* -------------------------------------------------------- the hero */}
        {/*
          Full-bleed, with the back arrow and the title floating over it
          rather than a header strip above it - the artwork's left half is
          empty on purpose, drawn that way to be sat on. A `PageHeader` here
          would put a white bar between the status bar and the picture and
          waste the composition.
        */}
        <View style={{ width: heroWidth, height: heroHeight }}>
          <Image
            source={HERO_ART}
            style={styles.heroArt}
            resizeMode="cover"
            /* Decoded to the size drawn and no Android cross-fade - the same
               treatment every other bundled photograph in the app gets. */
            resizeMethod="resize"
            fadeDuration={0}
          />

          {/*
            The title, on the artwork's own empty half.

            The banner was a picture with no words on it, so the page opened
            on a room and named itself only once the card below scrolled into
            view. The shot is composed with its left side clear precisely so
            something can sit there.
          */}
          <View style={[styles.heroCopy, { top: HERO_COPY_TOP + insets.top }]}>
            <View style={styles.heroBadge}>
              <Icon name="star" size={12} color={colors.onPrimary} />
              <Text variant="caption" color={colors.onPrimary}>
                MEMBERSHIP
              </Text>
            </View>

            {/*
              The name alone.

              The blurb that sat under this ran to two lines, and two lines
              are taller than the artwork's clear area - so the second one
              landed on the chair and the lamp, where no text colour reads.
              What it said ("save on every booking, with priority service")
              is the whole of the card immediately below, stated better.
            */}
            <Text variant="displaySm">Homingo+</Text>
          </View>
        </View>

        {/* -------------------------------------------------- why Homingo+ */}
        {/*
          Pulled up over the artwork's lower edge, so the card and the picture
          read as one block rather than as a photo with a card beneath it.
        */}
        <View style={styles.whyCard}>
          <Text variant="titleLg">Why Homingo+</Text>

          <View style={styles.highlights}>
            {highlights.map(item => (
              <View key={item.title} style={styles.highlight}>
                <View style={styles.highlightMark}>
                  <Icon name={item.icon} size={17} color={colors.primary} />
                </View>
                <View style={styles.highlightBody}>
                  <Text variant="caption">{item.title}</Text>
                  <Text variant="caption" color={colors.inkMuted}>
                    {item.detail}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {membership.active ? (
          <View style={styles.activeCard}>
            <Icon name="checkCircle" size={22} color={colors.success} />
            <View style={styles.activeBody}>
              <Text variant="subtitle">
                {membership.planName
                  ? `${membership.planName} is active`
                  : 'Your membership is active'}
              </Text>
              {membership.expiresOn ? (
                <Text variant="caption" color={colors.inkMuted}>
                  Valid until {membership.expiresOn}
                </Text>
              ) : null}
            </View>
          </View>
        ) : !subscription.signedIn ? (
          /*
             Said plainly, rather than as a network failure.

             Signed out, both subscription endpoints answer 401 and this card
             used to render "Plans aren't loading right now / Check your
             connection and try again" - wrong about the cause, and its Retry
             re-sent the same request that could never succeed.
          */
          <View style={styles.planCard}>
            <Text variant="subtitle">Sign in to see plans</Text>
            <Text variant="caption" color={colors.inkMuted}>
              Homingo+ pricing is tied to your account.
            </Text>
            <Pressable
              accessibilityRole="button"
              onPress={onSignIn}
              style={({ pressed }) => [
                styles.retryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="subtitle" color={colors.onPrimary}>
                Sign in
              </Text>
            </Pressable>
          </View>
        ) : subscription.plans.loading ? (
          <View style={styles.planCard}>
            <Skeleton width={120} height={20} radius={radii.sm} />
            <Skeleton width={160} height={34} radius={radii.sm} style={styles.skeletonGap} />
            <SkeletonText lines={3} style={styles.skeletonGap} />
          </View>
        ) : subscription.plans.error ? (
          <View style={styles.planCard}>
            <Text variant="subtitle">Plans aren't loading right now</Text>
            <Text variant="caption" color={colors.inkMuted}>
              Check your connection and try again.
            </Text>
            <Pressable
              accessibilityRole="button"
              onPress={subscription.plans.reload}
              style={({ pressed }) => [
                styles.retryButton,
                pressed ? styles.pressed : null,
              ]}
            >
              <Text variant="subtitle" color={colors.onPrimary}>
                Retry
              </Text>
            </Pressable>
          </View>
        ) : !chosen ? (
          <View style={styles.planCard}>
            <Text variant="subtitle">No plans available right now</Text>
          </View>
        ) : (
          <>
            {/* ------------------------------------------------- the toggle */}
            <View style={styles.segment}>
              {planViews.map(item => {
                const active = item.key === plan;
                return (
                  <Pressable
                    key={item.key}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={
                      item.saving > 0
                        ? `${item.label}, save ${item.saving} percent`
                        : item.label
                    }
                    onPress={() => setPlan(item.key)}
                    style={[styles.segmentTab, active ? styles.segmentTabOn : null]}
                  >
                    <Text
                      variant="subtitle"
                      align="center"
                      color={active ? colors.primary : colors.inkMuted}
                    >
                      {item.label}
                    </Text>

                    {/*
                      The saving rides on the TAB, not only on the card - it is
                      the reason to press a tab you are not currently on, so it
                      has to be legible from the other one.
                    */}
                    {item.saving > 0 ? (
                      <View style={styles.segmentFlag}>
                        <Text variant="caption" color={colors.onPrimary}>
                          Save {item.saving}%
                        </Text>
                      </View>
                    ) : null}
                  </Pressable>
                );
              })}
            </View>

            {/* --------------------------------------------------- the plan */}
            {/* One card, for the chosen term only - see the file note. */}
            <View style={styles.planCard}>
              {chosen.recommended ? (
                <View style={styles.bestValue}>
                  <Icon name="starFilled" size={12} color={colors.onPrimary} />
                  <Text variant="caption" color={colors.onPrimary}>
                    BEST VALUE
                  </Text>
                </View>
              ) : null}

              <Text variant="subtitle">{chosen.title}</Text>

              <View style={styles.priceRow}>
                <Text variant="displaySm">₹{rupees(chosen.price)}</Text>
                {/*
                  Twelve months at the monthly rate - a real price somebody
                  genuinely pays on the other term, not an invented "was".
                */}
                {chosen.was ? (
                  <Text
                    variant="bodySm"
                    color={colors.inkSubtle}
                    style={styles.was}
                  >
                    ₹{rupees(chosen.was)}
                  </Text>
                ) : null}
              </View>

              <Text variant="caption" color={colors.inkMuted}>
                {chosen.billed}
              </Text>

              <View style={styles.points}>
                {chosen.points.map(point => (
                  <View key={point} style={styles.point}>
                    <Icon name="check" size={15} color={colors.success} />
                    <Text variant="bodySm" style={styles.pointText}>
                      {point}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </>
        )}

        {/* ------------------------------------------------------ the perks */}
        <Text variant="titleLg" style={styles.heading}>
          Member perks
        </Text>

        <View style={styles.card}>
          {perks.map((perk, i) => (
            <View key={perk.title}>
              {i > 0 ? <View style={styles.separator} /> : null}
              <View style={styles.perk}>
                <View style={styles.perkMark}>
                  <Icon
                    name={perk.icon}
                    size={18}
                    color={colors.primary}
                    strokeWidth={1.9}
                  />
                </View>
                <View style={styles.perkBody}>
                  <Text variant="bodySm">{perk.title}</Text>
                  <Text variant="caption" color={colors.inkMuted}>
                    {perk.detail}
                  </Text>
                </View>
              </View>
            </View>
          ))}
        </View>

        {/* -------------------------------------------------------- the FAQ */}
        <Text variant="titleLg" style={styles.heading}>
          Frequently asked questions
        </Text>

        <View style={styles.faqList}>
          {faqs.map(faq => {
            const open = openFaq === faq.question;
            return (
              <View key={faq.question} style={styles.faq}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityState={{ expanded: open }}
                  accessibilityLabel={faq.question}
                  onPress={() => setOpenFaq(open ? null : faq.question)}
                  style={({ pressed }) => [
                    styles.faqHead,
                    pressed ? styles.pressed : null,
                  ]}
                >
                  <Text variant="bodySm" style={styles.faqQuestion}>
                    {faq.question}
                  </Text>
                  {/* Rotated rather than swapped for a second glyph, so the
                      arrow turns instead of jumping. */}
                  <Icon
                    name="chevronDown"
                    size={16}
                    color={colors.inkMuted}
                    style={open ? styles.chevronOpen : undefined}
                  />
                </Pressable>

                {open ? (
                  <Text
                    variant="caption"
                    color={colors.inkMuted}
                    style={styles.faqAnswer}
                  >
                    {faq.answer}
                  </Text>
                ) : null}
              </View>
            );
          })}
        </View>
      </ScrollView>

      {/*
        Back, in the corner, pushed below the status bar by hand - see the note
        on `insets` above. The earlier claim that the account's `SafeAreaView`
        covered this was wrong: the overlay these pages mount in never receives
        its padding, and the arrow sat under the clock.

        Outside the ScrollView, so it stays there once the plans are scrolled
        to. It used to float on the hero and leave with it.
      */}
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Back"
        onPress={onBack}
        hitSlop={12}
        style={({ pressed }) => [
          styles.backButton,
          { top: spacing.sm + insets.top },
          pressed ? styles.pressed : null,
        ]}
      >
        <Icon name="arrowLeft" size={22} color={colors.ink} strokeWidth={2} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    // No top padding: the hero is full-bleed and starts at the screen edge.
    // Nothing at the foot either - the CTA is a sibling below this scroller
    // with its own top padding, so anything here is a second gap on top of it.
    paddingBottom: 0,
    gap: spacing.md,
  },

  heroArt: {
    width: '100%',
    height: '100%',
  },
  /*
   * Tucked into the artwork's top-left corner.
   *
   * Positioned against the hero itself rather than laid out in a chrome row
   * across it - with the Premium pill gone there is nothing on the right to
   * balance against, and a full-width row would have left the arrow indented
   * by the page gutter instead of sitting in the corner.
   *
   * A light disc behind it because the artwork is pale here but it is still a
   * picture, and a bare glyph on one is legible only by luck.
   */
  backButton: {
    position: 'absolute',
    top: spacing.sm,
    left: spacing.sm,
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  /*
   * The title block, over the shot's clear left half.
   *
   * Held to 62% of the width: the right-hand third of every version of this
   * artwork carries the chair, the lamp and the plants, and copy run over
   * those is copy nobody can read.
   */
  /*
   * Held inside the shot's clear left half.
   *
   * Anchored to the TOP rather than the bottom: the artwork's plain area is
   * its upper left, and the furniture rises from the lower right, so a block
   * pinned to the bottom edge grows downward into exactly the part of the
   * picture that cannot carry text.
   *
   * `right: '46%'` keeps the run short enough that the title never reaches
   * the plant, at any type scale.
   */
  heroCopy: {
    position: 'absolute',
    /*
     * Inset further than the page gutter.
     *
     * The artwork's clear area does not start at its own left edge - a leaf
     * and the pale swash run down that side - so copy on the gutter sat on
     * the picture's border rather than in its empty middle-left.
     */
    left: screenPadding + spacing.xxxl + spacing.lg,
    right: '32%',
    top: HERO_COPY_TOP,
    gap: spacing.xs,
  },
  heroBadge: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
  },

  whyCard: {
    gap: spacing.md,
    marginHorizontal: screenPadding,
    // Rides up over the artwork's lower edge, so the two read as one block.
    marginTop: -spacing.xxxl,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...shadows.md,
  },
  /* Wraps rather than scrolls: four short pairs fit two-up on a narrow phone
     and four-up on anything wider, with no gesture to discover. */
  highlights: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  highlight: {
    // Two per row at the narrowest, less the gap between them.
    flexGrow: 1,
    flexBasis: '45%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    padding: spacing.sm,
    borderRadius: radii.md,
    ...hairline,
  },
  highlightMark: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  highlightBody: {
    flexShrink: 1,
  },

  activeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginHorizontal: screenPadding,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.successSurface,
  },
  activeBody: {
    flex: 1,
    gap: 2,
  },

  /* The two terms, as one control rather than two buttons. */
  segment: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginHorizontal: screenPadding,
    marginTop: spacing.sm,
    padding: spacing.xs,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
  },
  segmentTab: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.md,
    borderRadius: radii.pill,
  },
  segmentTabOn: {
    backgroundColor: colors.surface,
    ...shadows.sm,
  },
  /* Sits on the tab's top edge rather than in its flow, so the selected and
     unselected tabs stay the same height. */
  segmentFlag: {
    position: 'absolute',
    top: -9,
    right: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: 1,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceInverse,
  },

  planCard: {
    gap: spacing.xs,
    marginHorizontal: screenPadding,
    padding: spacing.lg,
    borderRadius: radii.lg,
    borderWidth: 1.5,
    borderColor: colors.primary,
    backgroundColor: colors.surface,
  },
  skeletonGap: {
    marginTop: spacing.sm,
  },
  retryButton: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
  },
  bestValue: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.primary,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: spacing.sm,
  },
  was: {
    textDecorationLine: 'line-through',
  },
  points: {
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  point: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  pointText: {
    flex: 1,
  },

  heading: {
    marginHorizontal: screenPadding,
    marginTop: spacing.md,
  },
  card: {
    marginHorizontal: screenPadding,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    overflow: 'hidden',
    ...hairline,
  },
  perk: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  perkMark: {
    width: 38,
    height: 38,
    borderRadius: radii.md,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
  },
  perkBody: {
    flex: 1,
    gap: 2,
  },
  separator: {
    height: 1,
    // Inset past the icon tile, so it separates rows rather than cutting the
    // card in half.
    marginLeft: spacing.md + 38 + spacing.md,
    backgroundColor: colors.border,
  },

  faqList: {
    gap: spacing.sm,
    marginHorizontal: screenPadding,
  },
  faq: {
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    overflow: 'hidden',
    ...hairline,
  },
  faqHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  faqQuestion: {
    flex: 1,
  },
  chevronOpen: {
    transform: [{ rotate: '180deg' }],
  },
  faqAnswer: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },

  /*
   * In the column, not floated over it - the same arrangement `CartBar` uses.
   *
   * Absolute positioning pinned this to the viewport, so it hung over the
   * page and never moved: scrolling to the last FAQ still left the strip
   * sitting on top of it. Laid out after the scroller instead, it is the foot
   * of the page - the ScrollView takes the space left over, the strip takes
   * what it needs, and the two cannot overlap by construction.
   *
   * The `flex: 1` on `scroller` is what makes this work: without it the
   * ScrollView sizes to its content and pushes the strip off the bottom.
   */
  pressed: {
    opacity: 0.6,
  },
});

export default SubscriptionScreen;
